import { FormItemProps } from 'antd';
import { useMemo, useState } from 'react';
import { RefFormHandler } from '.';
import { Order, OrderStatus } from '../../../services/orders-service/types';
import { Field, FieldType } from '../../../types';
import StringUtils from '../../../utils';
import UploadRawLabDataView from './UploadRawLabDataView';
import { useAuthStore } from '../../../store/auth-store';
import { Role } from '../../../services/auth-service/types';

const useStatusForm = ({
  order,
}: {
  order: Order;
  form: RefFormHandler | null;
}) => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [statusChange, setStatusChange] = useState(null);
  const { idToken } = useAuthStore();
  const cUser = useMemo(() => idToken.payload, [idToken]);

  const availableStatus = useMemo(() => {
    const toOptions = (value: OrderStatus) => ({
      value,
      label: StringUtils.capitalize(value.toLowerCase()),
    });

    if (
      order.status === OrderStatus.NEW ||
      order.status === OrderStatus.KIT_REGISTERED
    ) {
      return [OrderStatus.ORDER_APPROVED, OrderStatus.ORDER_REJECTED].map(
        toOptions,
      );
    }

    if (order.status === OrderStatus.ORDER_APPROVED) {
      if (order.orderType === 'retail') {
        return [
          OrderStatus.SPECIMEN_RECEIVED,
          OrderStatus.RECOLLECTION_REQUIRED,
        ].map(toOptions);
      }
      return [
        OrderStatus.KIT_SHIPPED,
        OrderStatus.SPECIMEN_RECEIVED,
        OrderStatus.RECOLLECTION_REQUIRED,
      ].map(toOptions);
    }

    if (order.status === OrderStatus.KIT_SHIPPED) {
      return [
        OrderStatus.SPECIMEN_RECEIVED,
        OrderStatus.RECOLLECTION_REQUIRED,
      ].map(toOptions);
    }

    if (order.status === OrderStatus.SPECIMEN_RECEIVED) {
      return [OrderStatus.COMPLETED, OrderStatus.RECOLLECTION_REQUIRED].map(
        toOptions,
      );
    }

    if (order.status === OrderStatus.RECOLLECTION_REQUIRED) {
      return [
        OrderStatus.KIT_SHIPPED,
        OrderStatus.SPECIMEN_RECEIVED,
        OrderStatus.RECOLLECTION_REQUIRED,
      ].map(toOptions);
    }

    if (order.status === OrderStatus.COMPLETED) {
      return [OrderStatus.RELEASE_APPROVED, OrderStatus.RELEASE_REJECTED].map(
        toOptions,
      );
    }

    return [].map(toOptions);
  }, [order.status, order.orderType]);

  const note = useMemo<Field>(
    () => ({
      key: 'note',
      type: FieldType.TextArea,
      inputProps: { placeholder: 'Leave a note' },
    }),
    [],
  );

  const secondFormFields = useMemo<Field[]>(() => {
    let validateStatus: FormItemProps['validateStatus'];
    if (uploadError) {
      validateStatus = 'error';
    }
    if (uploadSuccess) {
      validateStatus = 'success';
    }
    const uploadRawLabData: Field = {
      key: 'file',
      label: 'Upload raw lab data',
      type: FieldType.File,
      itemProps: {
        help: uploadError || uploadSuccess,
        validateStatus,
      },
      inputProps: ({ value }: { value: any }) => ({
        fileProps: {
          accept:
            '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
        },
        buttonLabel:
          value && value.length > 0 ? 'Replace CSV file' : 'Upload CSV file',
      }),
    };
    return [
      { key: 'sampleCollectionDate', type: FieldType.DateAndTime },
      { key: 'fastingStatus' },
      { key: 'divider3', type: FieldType.Divider },
      order.labResult?.s3Key
        ? {
            key: 'csvFile',
            label: 'Upload raw lab data',
            type: FieldType.Custom,
            render: () => <UploadRawLabDataView order={order} />,
          }
        : uploadRawLabData,
      note,
    ].map(field => ({
      ...field,
      disabled:
        order.status === OrderStatus.COMPLETED ||
        order.status === OrderStatus.RELEASE_REJECTED ||
        cUser['custom:roles'].includes('physician'),
    })) as Field[];
  }, [note, order, uploadError, uploadSuccess, cUser]);

  const fields = useMemo<Field[]>(() => {
    const changeStatus: Field = {
      key: 'status',
      label: 'Change Status',
      type: FieldType.Select,
      options: availableStatus,
      required: true,
      onChange: val => setStatusChange(val),
    };

    const approvedBy: Field = {
      key: 'orderApprovedBy',
      inputProps: { disabled: true },
    };

    const releaseRejectedBy: Field = {
      key: 'releaseRejectedBy',
      inputProps: { disabled: true },
    };

    let fieldArr: Field[] = [];

    switch (order.status) {
      case OrderStatus.NEW:
      case OrderStatus.KIT_REGISTERED:
        fieldArr = fieldArr
          .concat([
            changeStatus,
            { key: 'divider1', type: FieldType.Divider },
            note,
          ])
          .map(field => ({
            ...field,
            disabled: ['admin', 'eventmanager'].some(role =>
              cUser['custom:roles'].includes(role),
            ),
          }));
        break;

      case OrderStatus.ORDER_REJECTED:
      case OrderStatus.RELEASE_REJECTED:
        fieldArr = fieldArr.concat([
          changeStatus,
          { key: 'divider1', type: FieldType.Divider },
          note,
          { key: 'divider2', type: FieldType.Divider },
          approvedBy,
          releaseRejectedBy,
          { key: new Date().getTime().toString(), type: FieldType.Divider },
        ]);
        break;
      case OrderStatus.RECOLLECTION_REQUIRED:
      case OrderStatus.ORDER_APPROVED:
      case OrderStatus.KIT_SHIPPED:
        fieldArr = fieldArr
          .concat([
            approvedBy,
            { key: 'divider1', type: FieldType.Divider },
            changeStatus,
            { key: 'divider2', type: FieldType.Divider },
            note,
          ])
          .map(field => ({
            ...field,
            disabled: ['physician', 'eventmanager'].some(role =>
              cUser['custom:roles'].includes(role),
            ),
          }));
        break;
      case OrderStatus.SPECIMEN_RECEIVED:
        fieldArr = fieldArr
          .concat([
            approvedBy,
            { key: 'divider1', type: FieldType.Divider },
            changeStatus,
          ])
          .map(field => ({
            ...field,
            disabled: ['physician', 'eventmanager'].some(role =>
              cUser['custom:roles'].includes(role),
            ),
          }));
        break;
      case OrderStatus.COMPLETED:
        fieldArr = fieldArr
          .concat([
            approvedBy,
            { key: 'divider1', type: FieldType.Divider },
            changeStatus,
            { key: 'divider2', type: FieldType.Divider },
            note,
          ])
          .map(field => ({
            ...field,
            disabled: ['admin', 'eventmanager'].some(role =>
              cUser['custom:roles'].includes(role),
            ),
          }));
        break;
      case OrderStatus.RELEASE_APPROVED:
        fieldArr = fieldArr
          .concat([
            approvedBy,
            { key: 'divider1', type: FieldType.Divider },
            changeStatus,
          ])
          .concat(secondFormFields)
          .map(field => ({
            ...field,
            disabled:
              (field.key !== 'status' &&
                order.status === OrderStatus.COMPLETED) ||
              order.status === OrderStatus.RELEASE_APPROVED ||
              cUser['custom:roles'].includes('physician'),
          }));
        break;
      default:
        break;
    }

    return fieldArr;
  }, [availableStatus, note, order.status, secondFormFields, cUser]);

  const isOrderDetailsCompleted = useMemo<boolean>(() => {
    const { testKit, labResult } = order;
    const { s3Key } = labResult || {};
    const { sampleCollectionDate, fastingStatus } = testKit || {};
    return Boolean(s3Key && sampleCollectionDate && fastingStatus);
  }, [order]);

  const filledOutBy = useMemo(() => {
    if (
      [
        OrderStatus.NEW,
        OrderStatus.COMPLETED,
        OrderStatus.KIT_REGISTERED,
      ].includes(order.status)
    ) {
      return [Role.Physician, Role.SuperUser];
    }
    return [Role.Admin, Role.SuperUser];
  }, [order.status]);

  const filledOutByLabel = useMemo(() => {
    if (filledOutBy.includes(Role.Physician)) {
      return 'physician only';
    }
    return 'BHD representative only';
  }, [filledOutBy]);

  const canUpdateByRole = useMemo(() => {
    let canUpdate = false;
    const cRoles = cUser['custom:roles'].split(',');
    cRoles.forEach(role => {
      if (filledOutBy.includes(role as Role)) {
        canUpdate = true;
      }
    });
    return canUpdate;
  }, [cUser, filledOutBy]);

  const isButtonDisabled = useMemo(
    () =>
      (statusChange === OrderStatus.COMPLETED &&
        order.status === OrderStatus.SPECIMEN_RECEIVED &&
        !isOrderDetailsCompleted) ||
      !canUpdateByRole ||
      !statusChange,
    [statusChange, order.status, isOrderDetailsCompleted, canUpdateByRole],
  );

  const isOrderDetailsButtonDisabled = useMemo(
    () =>
      cUser['custom:roles'].includes('physician') ||
      cUser['custom:roles'].includes('eventmanager') ||
      ((cUser['custom:roles'].includes('admin') ||
        cUser['custom:roles'].includes('superuser')) &&
        isOrderDetailsCompleted),
    [cUser],
  );

  return {
    fields,
    setUploadError,
    setUploadSuccess,
    secondFormFields,
    isButtonDisabled,
    isOrderDetailsButtonDisabled,
    filledOutByLabel,
  };
};

export default useStatusForm;
